import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const createClientSkill: Skill = {
  id: "createClient",
  description:
    "Registra un cliente nuevo en un restaurante. Útil para 'agregar un cliente', 'registrar a Juan al sistema', etc. Requiere mínimo el nombre. Phone/email son opcionales pero recomendados para tracking.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      name: { type: "string", description: "Nombre completo del cliente" },
      phone: { type: "string", description: "Teléfono (opcional)" },
      email: { type: "string", description: "Email (opcional)" },
      notes: { type: "string", description: "Notas generales (opcional)" },
    },
    required: ["restaurantId", "name"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    const name = String(args.name ?? "").trim();
    if (!restaurantId || !name) {
      return { ok: false, error: "Faltan restaurantId o nombre." };
    }
    const phone = args.phone ? String(args.phone) : undefined;
    const email = args.email ? String(args.email) : undefined;
    const notes = args.notes ? String(args.notes) : undefined;

    const ok = await ctx.confirmation.confirm({
      title: "Registrar cliente",
      description: "Vas a agregar este cliente al restaurante.",
      details: [
        { label: "Nombre", value: name },
        ...(phone ? [{ label: "Teléfono", value: phone }] : []),
        ...(email ? [{ label: "Email", value: email }] : []),
      ],
      confirmLabel: "Registrar",
    });
    if (!ok) {
      return { ok: false, error: "Acción cancelada por el usuario." };
    }

    if (ctx.follow.enabled) {
      await ctx.follow.step(`Registrando a ${name}…`, { durationMs: 600 });
      await ctx.follow.clickAt(`[data-copilot-tab="clientes"]`, {
        durationMs: 700,
        label: "Clientes",
      });
      ctx.router.push(
        `/dashboard/restaurantes/${restaurantId}?tab=clientes`,
      );
      await sleep(900);
      await ctx.follow.step("Guardando cliente…", { durationMs: 500 });
    }

    try {
      const body: Record<string, unknown> = { name };
      if (phone) body.phone = phone;
      if (email) body.email = email;
      if (notes) body.notes = notes;

      const { data } = await apiWithAuth.post(
        `/restaurants/${restaurantId}/clients`,
        body,
        ctx.accessToken,
      );

      if (ctx.follow.enabled) {
        await ctx.follow.step(`✓ Cliente "${name}" registrado`, {
          durationMs: 900,
          spinner: false,
        });
        ctx.follow.clear();
      }

      emitCopilotMutation({
        scope: "clients",
        action: "created",
        restaurantId,
        id: data?.id,
        meta: { name },
      });

      return {
        ok: true,
        result: {
          id: data?.id,
          name: data?.name ?? name,
          phone: data?.phone,
          email: data?.email,
        },
        humanSummary: `Cliente "${name}" registrado.`,
      };
    } catch (err: any) {
      if (ctx.follow.enabled) {
        await ctx.follow.step("✗ Error al registrar", {
          durationMs: 700,
          spinner: false,
        });
        ctx.follow.clear();
      }
      return {
        ok: false,
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Error registrando cliente.",
      };
    }
  },
};
